import type { EntitlementDecision } from '@tabbio/types';

/** Mirrors `SUBSCRIPTION_BLOCKED_TOOL_NAME` in hooks/agent-stream.ts. */
const SEND_BLOCKED_TOOL = '__subscriptionBlocked';

/**
 * The live tool results that should open the paywall (fair use,
 * 2026-09-25): a message the server refused because the credits are spent
 * (`__subscriptionBlocked`, made from the 403), and a tool refused inside a
 * reply for credits, a daily quota or the plan (`{ ok: false, error: { code:
 * 'SUBSCRIPTION_ENTITLEMENT_DENIED', decision } }`). Anything else: null.
 */
export type ChatPaywallTrigger = {
  decision: EntitlementDecision;
  /** `now`: nothing is streaming (the send itself was refused). `after-reply`: let the reply finish first. */
  timing: 'now' | 'after-reply';
  entryPoint: 'chat.send_blocked' | 'chat.tool_denied';
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Walls a person in the chat cannot lift by upgrading (an employer seat or workspace pool). */
const NOT_SELF_SERVE = new Set(['workspace_ai_limit_reached', 'seat_required', 'owner_required']);

function readDecision(value: unknown): EntitlementDecision | null {
  if (!isRecord(value) || typeof value.reasonCode !== 'string') return null;
  if (NOT_SELF_SERVE.has(value.reasonCode)) return null;
  return value as unknown as EntitlementDecision;
}

export function chatPaywallTrigger(activity: { toolName: string; result?: unknown }): ChatPaywallTrigger | null {
  const result = activity.result;
  if (!isRecord(result)) return null;
  if (activity.toolName === SEND_BLOCKED_TOOL) {
    const decision = readDecision(result.decision);
    return decision ? { decision, timing: 'now', entryPoint: 'chat.send_blocked' } : null;
  }
  const error = isRecord(result.error) ? result.error : null;
  if (result.ok !== false || !error || error.code !== 'SUBSCRIPTION_ENTITLEMENT_DENIED') return null;
  const decision = readDecision(error.decision);
  return decision ? { decision, timing: 'after-reply', entryPoint: 'chat.tool_denied' } : null;
}
